import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Instagram, Music2, Facebook, MessageCircle } from "lucide-react";
import { useStoreInfo, INFO_PAGES } from "@/context/StoreInfoContext";
import { LOCAL_PAGES } from "@/lib/localPages";

const Col = ({ title, children }) => (
  <div>
    <div className="font-overline text-[10px] text-[#D4AF37] mb-4">— {title}</div>
    {children}
  </div>
);

export const Footer = () => {
  const { info } = useStoreInfo();
  if (!info) return null;
  const wa = (info.whatsapp || "").replace(/\D/g, "");

  return (
    <footer className="bg-black border-t border-white/10 text-white mt-16" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <Col title="POWER FITNESS NUTRITION">
          <div className="font-display font-black text-xl leading-none mb-3">POWER FITNESS<span className="text-[#D4AF37]"> NUTRITION</span></div>
          <p className="text-xs text-neutral-400 leading-relaxed mb-4">Premium Supplement Store. Integratori selezionati, consigli onesti e un programma fedeltà che premia chi si allena.</p>
          <div className="flex gap-2">
            {info.instagram && <a href={`https://instagram.com/${info.instagram}`} target="_blank" rel="noreferrer" className="w-9 h-9 border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center transition-colors" aria-label="Instagram" data-testid="footer-instagram"><Instagram className="w-4 h-4" /></a>}
            {info.tiktok && <a href={`https://tiktok.com/@${info.tiktok}`} target="_blank" rel="noreferrer" className="w-9 h-9 border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center transition-colors" aria-label="TikTok" data-testid="footer-tiktok"><Music2 className="w-4 h-4" /></a>}
            {info.facebook && <a href={info.facebook.startsWith("http") ? info.facebook : `https://facebook.com/${info.facebook}`} target="_blank" rel="noreferrer" className="w-9 h-9 border border-white/15 hover:border-[#D4AF37] hover:text-[#D4AF37] flex items-center justify-center transition-colors" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>}
          </div>
        </Col>

        <Col title="I NOSTRI NEGOZI">
          <ul className="space-y-3">
            {(info.stores || []).map((s) => (
              <li key={s.id} className="flex items-start gap-2 text-xs text-neutral-300" data-testid={`footer-store-${s.id}`}>
                <MapPin className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 shrink-0" />
                <div>
                  <div className="font-bold text-white">{s.label}</div>
                  <a href={`https://maps.google.com/?q=${encodeURIComponent(s.address)}`} target="_blank" rel="noreferrer" className="hover:text-[#D4AF37]">{s.address}</a>
                  {s.hours && <div className="text-neutral-500">{s.hours}</div>}
                </div>
              </li>
            ))}
          </ul>
        </Col>

        <Col title="INFORMAZIONI">
          <ul className="space-y-2">
            <li><Link to="/blog" className="text-xs text-neutral-300 hover:text-[#D4AF37] transition-colors" data-testid="footer-link-blog">Consigli & Guide</Link></li>
            {INFO_PAGES.map((p) => (
              <li key={p.slug}><Link to={p.path} className="text-xs text-neutral-300 hover:text-[#D4AF37] transition-colors" data-testid={`footer-link-${p.slug}`}>{p.label}</Link></li>
            ))}
          </ul>
          <div className="font-overline text-[10px] text-neutral-500 mt-5 mb-2">INTEGRATORI IN SICILIA</div>
          <ul className="space-y-1.5">
            {LOCAL_PAGES.map((p) => (
              <li key={p.slug}><Link to={p.path} className="text-xs text-neutral-300 hover:text-[#D4AF37] transition-colors" data-testid={`footer-local-${p.slug}`}>Integratori {p.slug === "sicilia" ? "in Sicilia" : `a ${p.city}`}</Link></li>
            ))}
          </ul>
        </Col>

        <Col title="CONTATTI">
          <ul className="space-y-2.5 text-xs text-neutral-300">
            {info.phone && <li className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-[#D4AF37]" /><a href={`tel:${(info.phone || "").replace(/\s/g, "")}`} className="hover:text-[#D4AF37]" data-testid="footer-phone">{info.phone}</a></li>}
            {wa && <li className="flex items-center gap-2"><MessageCircle className="w-3.5 h-3.5 text-[#D4AF37]" /><a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="hover:text-[#D4AF37]" data-testid="footer-whatsapp">WhatsApp</a></li>}
            {info.email && <li className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#D4AF37]" /><a href={`mailto:${info.email}`} className="hover:text-[#D4AF37] break-all" data-testid="footer-email">{info.email}</a></li>}
          </ul>
        </Col>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between text-[11px] text-neutral-500" data-testid="footer-legal">
          <div>© {new Date().getFullYear()} {info.business_name} · P.IVA {info.vat}{info.rea ? ` · REA ${info.rea}` : ""}{info.cf ? ` · C.F. ${info.cf}` : ""}{info.pec ? ` · PEC ${info.pec}` : ""}</div>
          <div>Tutti i prezzi sono IVA inclusa</div>
        </div>
      </div>
    </footer>
  );
};
